/**
 * Message formatting with no catalog attached.
 *
 * Separate from ./index so a Client Component can format the strings it was handed
 * as props — live counts need pluralising in the browser — without importing, and
 * shipping, every catalog.
 */

export type Vars = Record<string, string | number>;

export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

/**
 * Build `t` and `plural` over a flat message map: the whole catalog on the server, or
 * the subset a page passed to the browser. A missing key renders as itself, which is
 * visible in review rather than silently blank.
 */
export function formatter(locale: string, messages: Readonly<Record<string, string | undefined>>) {
  const rules = new Intl.PluralRules(locale);
  const ordinals = new Intl.PluralRules(locale, { type: "ordinal" });
  const numbers = new Intl.NumberFormat(locale);
  const lookup = (key: string) => messages[key] ?? key;

  return {
    t: (key: string, vars?: Vars) => interpolate(lookup(key), vars),
    plural: (key: string, count: number, vars?: Vars) => {
      const form = `${key}.${rules.select(count)}`;
      const template = messages[form] ?? lookup(`${key}.other`);
      return interpolate(template, { count: numbers.format(count), ...vars });
    },
    /** "1st", "2nd", "3rd", "4th": `<key>.one|two|few|other`, with `{rank}`. */
    ordinal: (key: string, rank: number, vars?: Vars) => {
      const form = `${key}.${ordinals.select(rank)}`;
      const template = messages[form] ?? lookup(`${key}.other`);
      return interpolate(template, { rank: numbers.format(rank), ...vars });
    },
  };
}

/**
 * Pick the best of `available` for a BCP 47 tag or an Accept-Language header.
 *
 * Exact language matches only, in preference order; region subtags are ignored since
 * no catalog is regional. Anything unmatched is `fallback`.
 */
export function negotiate<L extends string>(
  requested: string | null | undefined,
  available: readonly L[],
  fallback: L,
): L {
  if (!requested) return fallback;
  const preferred = requested
    .split(",")
    .map((part) => {
      const [tag = "", ...params] = part.trim().split(";");
      const q = params.find((p) => p.trim().startsWith("q="));
      return { lang: tag.split("-")[0]!.toLowerCase(), q: q ? Number(q.split("=")[1]) : 1 };
    })
    .filter((p) => p.lang && p.q > 0)
    .sort((a, b) => b.q - a.q);
  for (const { lang } of preferred) {
    const match = available.find((l) => l === lang);
    if (match) return match;
  }
  return fallback;
}

/** The keys of `reference` that `catalog` covers completely — i.e. can be offered. */
export function isComplete(reference: Record<string, string>, catalog: Record<string, string | undefined>): boolean {
  return Object.keys(reference).every((k) => typeof catalog[k] === "string");
}
